import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Loader2, Lock, Phone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { isStrongPassword } from '@/lib/accountValidation';

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const normalizeIdentifier = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (isEmail(trimmed)) return trimmed.toLowerCase();

  let phone = trimmed.replace(/[\s-]/g, '');
  if (phone.endsWith('+') && !phone.startsWith('+')) {
    phone = `+${phone.slice(0, -1)}`;
  }
  if (phone.startsWith('+9630')) {
    phone = `+963${phone.substring(5)}`;
  } else if (phone.startsWith('009630')) {
    phone = `00963${phone.substring(6)}`;
  } else if (phone.startsWith('09')) {
    phone = `+963${phone.substring(1)}`;
  } else if (/^\d+$/.test(phone) && !phone.startsWith('963')) {
    phone = `+963${phone}`;
  } else if (phone.startsWith('963') && !phone.startsWith('+963')) {
    phone = `+${phone}`;
  }

  return phone;
};

const isValidIdentifier = (value) => {
  const normalized = normalizeIdentifier(value);
  if (!normalized) return false;
  if (isEmail(normalized)) return true;
  return /^(\+|00)?\d{8,15}$/.test(normalized);
};

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const getAuthenticatedRoute = (userType) =>
  userType === 'doctor' ? '/doctor/dashboard' : '/appointments';

const ForgotPasswordPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { completePasswordReset } = useAuth();
  const lang = (i18n.language || 'ar').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isRTL = lang === 'ar';

  const [step, setStep] = useState('request');
  const [partnerType, setPartnerType] = useState('patient');
  const [identifier, setIdentifier] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const labels = lang === 'ar'
    ? {
        requestTitle: 'نسيت كلمة المرور؟',
        requestDescription: 'سنرسل رمز تحقق OTP لإعادة تعيين كلمة المرور.',
        resetTitle: 'إعادة تعيين كلمة المرور',
        resetDescription: 'أدخل رمز التحقق وكلمة المرور الجديدة.',
        patient: 'مريض',
        doctor: 'طبيب',
        identifier: 'رقم الهاتف أو البريد الإلكتروني',
        identifierPlaceholder: '+963999123456 أو example@email.com',
        sendOtp: 'إرسال رمز التحقق',
        sending: 'جاري الإرسال...',
        otpSent: 'تم إرسال رمز التحقق',
        passwordUpdated: 'تم تحديث كلمة المرور بنجاح',
        identifierRequired: 'يرجى إدخال رقم الهاتف أو البريد الإلكتروني',
        identifierInvalid: 'يرجى إدخال رقم هاتف أو بريد صحيح',
        otpCode: 'رمز التحقق',
        otpRequired: 'يرجى إدخال رمز التحقق',
        otpTooShort: 'رمز التحقق قصير جداً',
        newPassword: 'كلمة مرور جديدة',
        confirmPassword: 'تأكيد كلمة المرور',
        passwordRequired: 'يرجى إدخال كلمة مرور جديدة',
        passwordTooShort: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
        passwordsMismatch: 'كلمتا المرور غير متطابقتين',
        resetPassword: 'تغيير كلمة المرور',
        updating: 'جاري التحديث...',
        resendOtp: 'إعادة إرسال الرمز',
        backToRequest: 'تغيير الرقم',
        backToLogin: 'العودة لتسجيل الدخول',
        failedToSendOtp: 'فشل إرسال رمز التحقق',
        failedToReset: 'فشل تغيير كلمة المرور',
      }
    : {
        requestTitle: 'Forgot Password?',
        requestDescription: 'We will send an OTP code to reset your password.',
        resetTitle: 'Reset Password',
        resetDescription: 'Enter the OTP code and your new password.',
        patient: 'Patient',
        doctor: 'Doctor',
        identifier: 'Phone or Email',
        identifierPlaceholder: '+963999123456 or example@email.com',
        sendOtp: 'Send OTP Code',
        sending: 'Sending...',
        otpSent: 'Verification code has been sent',
        passwordUpdated: 'Password updated successfully',
        identifierRequired: 'Please enter your phone or email',
        identifierInvalid: 'Please enter a valid phone number or email',
        otpCode: 'OTP Code',
        otpRequired: 'Please enter OTP code',
        otpTooShort: 'OTP code is too short',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        passwordRequired: 'Please enter a new password',
        passwordTooShort: 'Password must be at least 6 characters',
        passwordsMismatch: 'Passwords do not match',
        resetPassword: 'Reset Password',
        updating: 'Updating...',
        resendOtp: 'Resend OTP',
        backToRequest: 'Change identifier',
        backToLogin: 'Back to Login',
        failedToSendOtp: 'Failed to send OTP code',
        failedToReset: 'Failed to reset password',
      };

  const validateIdentifier = () => {
    if (!identifier.trim()) {
      toast.error(labels.identifierRequired);
      return null;
    }

    if (!isValidIdentifier(identifier)) {
      toast.error(labels.identifierInvalid);
      return null;
    }

    return normalizeIdentifier(identifier);
  };

  const requestResetCode = async ({ resend = false } = {}) => {
    const normalizedIdentifier = validateIdentifier();
    if (!normalizedIdentifier) return;

    if (resend) {
      setResending(true);
    } else {
      setLoading(true);
    }

    try {
      await authService.sendResetLink(normalizedIdentifier, partnerType);
      setIdentifier(normalizedIdentifier);
      setStep('reset');
      toast.success(labels.otpSent);
    } catch (error) {
      toast.error(extractErrorMessage(error, labels.failedToSendOtp));
    } finally {
      setLoading(false);
      setResending(false);
    }
  };

  const handleRequestSubmit = (event) => {
    event.preventDefault();
    requestResetCode();
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();

    const normalizedIdentifier = validateIdentifier();
    if (!normalizedIdentifier) return;

    const normalizedOtp = otpCode.trim();
    if (!normalizedOtp) {
      toast.error(labels.otpRequired);
      return;
    }
    if (normalizedOtp.length < 4) {
      toast.error(labels.otpTooShort);
      return;
    }
    if (!newPassword) {
      toast.error(labels.passwordRequired);
      return;
    }
    if (!isStrongPassword(newPassword)) {
      toast.error(labels.passwordTooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(labels.passwordsMismatch);
      return;
    }

    setLoading(true);
    try {
      const result = await completePasswordReset({
        phoneOrEmail: normalizedIdentifier,
        otpCode: normalizedOtp,
        newPassword,
        userType: partnerType,
      });

      if (!result.success) {
        toast.error(result.error || labels.failedToReset);
        return;
      }

      toast.success(labels.passwordUpdated);
      navigate(result.needsLogin ? '/login' : getAuthenticatedRoute(result.userType || partnerType), {
        replace: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="forgot-password-page"
    >
      <Card className="w-full max-w-md dark:bg-slate-800 border-0 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mb-4">
            {step === 'request' ? (
              <Phone className="h-8 w-8 text-teal-600" />
            ) : (
              <KeyRound className="h-8 w-8 text-teal-600" />
            )}
          </div>
          <CardTitle className="text-2xl dark:text-white">
            {step === 'request' ? labels.requestTitle : labels.resetTitle}
          </CardTitle>
          <CardDescription className="dark:text-slate-400">
            {step === 'request' ? labels.requestDescription : labels.resetDescription}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          {step === 'request' ? (
            <Tabs value={partnerType} onValueChange={setPartnerType} className="mb-5">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="patient">{labels.patient}</TabsTrigger>
                <TabsTrigger value="doctor">{labels.doctor}</TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null}

          {step === 'request' ? (
            <form onSubmit={handleRequestSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phoneOrEmail" className="dark:text-slate-300">
                  {labels.identifier}
                </Label>
                <div className="relative">
                  <Phone className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} h-5 w-5 text-slate-400`} />
                  <Input
                    id="phoneOrEmail"
                    type="text"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    placeholder={labels.identifierPlaceholder}
                    className={`${isRTL ? 'pr-10' : 'pl-10'} dark:bg-slate-700 dark:text-white dark:border-slate-600`}
                    dir="ltr"
                    required
                    data-testid="phone-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5 animate-spin`} />
                    {labels.sending}
                  </>
                ) : (
                  labels.sendOtp
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resetIdentifier" className="dark:text-slate-300">
                  {labels.identifier}
                </Label>
                <div className="relative">
                  <Phone className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} h-5 w-5 text-slate-400`} />
                  <Input
                    id="resetIdentifier"
                    type="text"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    className={`${isRTL ? 'pr-10' : 'pl-10'} dark:bg-slate-700 dark:text-white dark:border-slate-600`}
                    dir="ltr"
                    required
                    data-testid="reset-identifier-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="otpCode" className="dark:text-slate-300">
                  {labels.otpCode}
                </Label>
                <Input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="- - - - - -"
                  className="text-center text-2xl font-mono dark:bg-slate-700 dark:text-white dark:border-slate-600"
                  maxLength={6}
                  dir="ltr"
                  required
                  data-testid="otp-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="dark:text-slate-300">
                  {labels.newPassword}
                </Label>
                <div className="relative">
                  <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} h-5 w-5 text-slate-400`} />
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    className={`${isRTL ? 'pr-10' : 'pl-10'} dark:bg-slate-700 dark:text-white dark:border-slate-600`}
                    required
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="dark:text-slate-300">
                  {labels.confirmPassword}
                </Label>
                <div className="relative">
                  <Lock className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} h-5 w-5 text-slate-400`} />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className={`${isRTL ? 'pr-10' : 'pl-10'} dark:bg-slate-700 dark:text-white dark:border-slate-600`}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6"
                disabled={loading}
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5 animate-spin`} />
                    {labels.updating}
                  </>
                ) : (
                  labels.resetPassword
                )}
              </Button>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestResetCode({ resend: true })}
                  disabled={resending || loading}
                  className="dark:border-slate-600 dark:text-slate-300"
                >
                  {resending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                      {labels.resendOtp}
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep('request')}
                  disabled={loading}
                  className="dark:text-slate-300"
                >
                  {labels.backToRequest}
                </Button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 dark:text-teal-400"
              data-testid="back-to-login"
            >
              <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
              {labels.backToLogin}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
